import { firebaseAuth } from '../firebase/auth';
import { DailyTask } from '../../types/goals';
import { parsedParamsUrlForPagination } from '../utils';

const API_URL = import.meta.env.VITE_API_URL;

interface TaskFilters {
    page?: number;
    limit?: number;
    goalId?: string;
    milestoneId?: string;
    isCompleted?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

interface TasksResponse {
    tasks: DailyTask[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    stats?: {
        total: number;
        completedTasks: number;
        overdueTasks: number;
    };
}

class TaskAPI {
    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const idToken = await firebaseAuth.getIdToken();
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
            ...options.headers
        };

        try {
            const response = await fetch(`${API_URL}${endpoint}`, {
                ...options,
                headers
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || 'Request failed');
            }

            return response.json();
        } catch (error) {
            console.error(`API Error (${endpoint}):`, error);
            throw error;
        }
    }

    async getTasks(filters: TaskFilters = {}): Promise<{
        tasks: DailyTask[];
        pagination: {
            total: number;
            page: number;
            limit: number;
            totalPages: number;
        };
        stats?: {
            total: number;
            completedTasks: number;
            overdueTasks: number;
        };
    }> {
        const queryParams = this.buildQueryParams(filters);
        const response = await this.request<TasksResponse>(`/api/dailytasks?${queryParams}`);

        return {
            tasks: response.tasks.map(this.parseTaskDates),
            pagination: response.pagination,
            stats: response.stats
        };
    }

    async getTask(taskId: string): Promise<DailyTask> {
        const response = await this.request<DailyTask>(`/api/dailytasks/${taskId}`);
        return this.parseTaskDates(response);
    }

    async createTask(data: {
        goalId: string;
        milestoneId: string;
        title: string;
        description?: string;
        date: Date;
        isCompleted?: boolean;
    }): Promise<{ task: DailyTask; stats?: { total: number; completedTasks: number; overdueTasks: number } }> {
        const response = await this.request<any>('/api/dailytasks', {
            method: 'POST',
            body: JSON.stringify({
                ...data,
                date: data.date.toISOString()
            })
        });

        return {
            task: this.parseTaskDates(response),
            stats: response.stats
        };
    }

    async updateTask(taskId: string, updates: Partial<DailyTask>): Promise<DailyTask> {
        const response = await this.request<DailyTask>(`/api/dailytasks/${taskId}`, {
            method: 'PATCH',
            body: JSON.stringify(updates)
        });
        return this.parseTaskDates(response);
    }

    async deleteTask(taskId: string): Promise<{ message: string; task: DailyTask }> {
        return this.request<{ message: string; task: DailyTask }>(`/api/dailytasks/${taskId}`, {
            method: 'DELETE'
        });
    }

    async toggleTaskCompletion(taskId: string): Promise<DailyTask> {
        const response = await this.request<DailyTask>(`/api/dailytasks/${taskId}/toggle`, {
            method: 'PATCH',
        });
        return this.parseTaskDates(response);
    }

    private buildQueryParams(filters: TaskFilters): string {
        return parsedParamsUrlForPagination(filters);
    }

    private parseTaskDates(task: any): DailyTask {
        if (!task) return task;
        return {
            ...task,
            date: new Date(task.date),
            createdAt: new Date(task.createdAt),
            updatedAt: task.updatedAt ? new Date(task.updatedAt) : undefined
        };
    }
}

export const dailyTasksAPI = new TaskAPI();
